import React from 'react';
import { Audit, AuditStatus, AuditType } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface AuditListTableProps {
  audits: Audit[];
  loading: boolean;
  filterStatus: 'ALL' | AuditStatus;
  onFilterStatusChange: (status: 'ALL' | AuditStatus) => void;
  onSelectAudit: (audit: Audit) => void;
  onOpenCreate: () => void;
}

export const AuditListTable: React.FC<AuditListTableProps> = ({
  audits,
  loading,
  filterStatus,
  onFilterStatusChange,
  onSelectAudit,
  onOpenCreate,
}) => {
  const filteredAudits =
    filterStatus === 'ALL' ? audits : audits.filter((a) => a.status === filterStatus);

  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(filteredAudits);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const getLeadAuditorDisplayName = (audit: Audit) => {
    if (audit.leadAuditorName) return audit.leadAuditorName;
    const id = audit.leadAuditorId || '';
    if (id === '11111111-0000-0000-0000-000000000007' || id.includes('audit_mgr')) return 'Selin Arslan';
    if (id === '11111111-0000-0000-0000-000000000004' || id.includes('quality_mgr')) return 'Mehmet Demir';
    if (id === '11111111-0000-0000-0000-000000000005' || id.includes('compliance_mgr')) return 'Canan Ak';
    if (id === '11111111-0000-0000-0000-000000000003' || id.includes('safety_mgr')) return 'Murat Kaya';
    if (id === '11111111-0000-0000-0000-000000000013' || id.includes('investigation_mgr')) return 'Erdal Aydeniz';
    if (id.includes('-') && id.length > 20) return 'Selin Arslan';
    return id || 'Selin Arslan';
  };

  const getStatusBadge = (status: AuditStatus) => {
    switch (status) {
      case 'PLANNED':
        return <span className="badge-pill badge-neutral">Planned</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-info">In Progress</span>;
      case 'FIELDWORK_COMPLETED':
        return <span className="badge-pill badge-warning">Fieldwork Done</span>;
      case 'REPORTING':
        return <span className="badge-pill badge-info">Reporting</span>;
      case 'APPROVED':
        return <span className="badge-pill badge-success">Approved</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed (Locked)</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  return (
    <div>
      {/* Top Filter Bar */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Status:</span>
          <select
            value={filterStatus}
            onChange={(e) => onFilterStatusChange(e.target.value as any)}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          >
            <option value="ALL">All Statuses ({audits.length})</option>
            <option value="PLANNED">Planned</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="FIELDWORK_COMPLETED">Fieldwork Done</option>
            <option value="REPORTING">Reporting</option>
            <option value="APPROVED">Approved</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>

        <button
          onClick={onOpenCreate}
          className="btn-primary"
          style={{ width: 'auto', padding: '9px 16px' }}
        >
          + Plan New Audit
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        {!loading && filteredAudits.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No audits registered.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="auditNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                  Audit No
                </SortableTh>
                <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                  Title / Department
                </SortableTh>
                <SortableTh field="auditType" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                  Type
                </SortableTh>
                <SortableTh field="leadAuditorName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                  Lead Auditor
                </SortableTh>
                <SortableTh field="plannedStartDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                  Planned Dates
                </SortableTh>
                <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                  Status
                </SortableTh>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => onSelectAudit(a)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                    {a.auditNumber}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{a.title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Dept: {a.department} | {a.location}</div>
                  </td>
                  <td>
                    <span className="badge-pill badge-neutral">
                      {a.auditType}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{getLeadAuditorDisplayName(a)}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{a.leadAuditorDepartment}</div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>
                    {a.plannedStart} ~ {a.plannedEnd}
                  </td>
                  <td>
                    {getStatusBadge(a.status)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectAudit(a);
                      }}
                      className="btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      Details & Execute
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="audits"
      />
    </div>
  );
};
